import { strings } from './strings';
import { getAppSyncErrorType } from '../../libs/utils/getAppSyncErrorType';

const MESSAGES = new Map<string, (id: string | undefined) => string>([
  ['Unauthorized', () => strings.errors.unauthorized],
  ['UnreadableLabel', () => strings.errors.unreadableLabel],
  ['LabelConflict', () => strings.errors.labelConflict],
  ['LabelNotFound', strings.errors.labelNotFound],
  ['BatchNotFound', strings.errors.batchNotFound],
  ['ItemNotFound', () => strings.errors.itemNotFound],
  ['UnsupportedUnit', () => strings.errors.unsupportedUnit],
  ['NotCreator', () => strings.errors.notCreator],
  ['EditWindowClosed', () => strings.errors.editWindowClosed],
  ['BatchDeleted', () => strings.errors.batchDeleted],
  ['HasChildren', () => strings.errors.hasChildren],
  ['TooLarge', () => strings.errors.tooLarge],
  ['ValidationError', () => strings.errors.validation],
  ['Conflict', () => strings.errors.conflict]
]);

/** The text a failed batch tracking operation shows, chosen by the AppSync `errorType`; `args.id` names the batch or label when the caller has one. */
export const batchTrackingErrorMessage = (error: unknown, args?: { id?: string }): string => {
  const errorType = getAppSyncErrorType(error);
  const message = errorType === undefined ? undefined : MESSAGES.get(errorType);
  return message ? message(args?.id) : strings.errors.generic;
};

/** The text of a failed read of one batch by an ID from the URL: a malformed ID is told apart from an unregistered one. */
export const readErrorMessage = (error: unknown, id: string): string =>
  getAppSyncErrorType(error) === 'ValidationError' ? strings.batch.invalidId : batchTrackingErrorMessage(error, { id });

const MANUAL_BATCH_MESSAGES = new Map([
  ['LabelConflict', strings.errors.manualLabelConflict],
  ['ItemNotFound', strings.errors.manualItemNotFound],
  ['BatchNotFound', strings.errors.manualBatchNotFound]
]);

/** The text of a failed record, edit or delete of a manual batch; where the batch tracking text speaks of labels it says its own. */
export const manualBatchErrorMessage = (error: unknown): string => {
  const errorType = getAppSyncErrorType(error);
  return (errorType === undefined ? undefined : MANUAL_BATCH_MESSAGES.get(errorType)) ?? batchTrackingErrorMessage(error);
};

const BATCH_STATE_REFUSALS = new Set(['NotCreator', 'EditWindowClosed', 'BatchDeleted', 'HasChildren', 'BatchNotFound']);

/** The server refused because of the batch's own state, which the screen may have wrong: it should read the batch again. */
export const refusedByBatchState = (error: unknown): boolean => BATCH_STATE_REFUSALS.has(getAppSyncErrorType(error) ?? '');
